import {
  Affiliation,
  AssetStatus,
  Member,
  ProductType,
  VehicleType,
} from "../../types";
import {
  BillingDetails,
  Customer,
  DriversClub,
  FeatureFlags,
  PolicyDetails,
} from "../../types";
import {
  apexAutopayBill,
  apexAutopayFailedBill,
  apexPastDueBill,
  apexUpcomingBill,
  autopayCoreBill,
  autopayFailedCoreBill,
  hdcAutopayFailedBill,
  hdcPastDueBill,
  paidCoreBill,
  pastDueCoreRevalBill,
} from "../../mock";
import {
  DriverV2,
  PolicyAddressV2,
  PolicyCoverageV2,
} from "@hagerty/customer-insurance-gateway-client";

export type Persona = {
  /** Unique key used for DevTools */
  key: string;
  description: string;
  customer: Customer;
  billing: BillingDetails;
  contactId: string;
  policies: PolicyDetails[];
  hdc: DriversClub;
  featureFlags: Partial<FeatureFlags>;
};

export const member: Partial<Member> = {
  __typename: "CustomerMember",
  id: "HDC00208785",
  assets: [
    {
      id: "02i8W000007oV9jQAE",
      status: AssetStatus.ACTIVE,
      affiliation: Affiliation.HAGERTY,
      amountPaid: {
        amount: 70,
      },
      activatedDate: "2022-06-18",
      effectiveAt: "2022-06-18",
      expireAt: "2023-06-18",
      omniOrderId: "1234",
      product: {
        id: "01t1I000003ATAFQA4",
        type: ProductType.MEMBERSHIP,
        code: "USMEMB2",
        price: {
          amount: 70,
        },
        isVIP: false,
        level: 2,
        benefits: [
          {
            __typename: "CustomerMemberRoadsideBenefit",
            included: true,
            towingDistance: 100,
            amountCovered: 100,
            vehicleType: VehicleType.CLASSIC,
          },
          {
            __typename: "CustomerMemberMagazineBenefit",
            included: true,
            issues: 6,
          },
          {
            __typename: "CustomerMemberHvtBenefit",
            included: true,
          },
          {
            __typename: "CustomerMemberEventsBenefit",
            included: true,
          },
        ],
      },
    },
  ],
};

const policyAddress: PolicyAddressV2 = {
  lineOne: "385 GLENDALE ST",
  lineTwo: "",
  city: "Dakota",
  county: "Stephenson",
  stateRegion: "IL",
  postalCode: "61018-9747",
  country: "US",
};

const drivers: DriverV2[] = [
  {
    name: {
      prefix: "Mr.",
      first: "Mocked",
      last: "Driver",
    },
    memberId: 123,
    dateOfBirth: "1964-03-01T00:00:00",
    isVehicleOperator: true,
    driverType: "PRIMARY",
  },
  {
    name: {
      prefix: "Ms.",
      first: "Mocked",
      last: "Driver too",
    },
    memberId: 234,
    dateOfBirth: "1967-11-01T00:00:00",
    isVehicleOperator: false,
    driverType: "SECONDARY",
  },
];

const policyCoverages: PolicyCoverageV2[] = [
  {
    name: "Liability - CSL",
    limit: "$300000",
    type: 1,
  },
  {
    name: "Uninsured Motorists BI Single Limit",
    limit: "$300000",
    type: 6,
  },
  {
    name: "Underinsured Motorists BI Single Limit",
    limit: "$300000",
    type: 7,
  },
  {
    name: "Medical Payments",
    limit: "$5000",
    type: 12,
  },
];

const jayLeno: Persona = {
  key: "jayLeno",
  description:
    "🇺🇸 | Multiple policies, one with broker, two enth+ | HDC 1.5 3rd Gear | All alerts",
  customer: {
    id: "7cf9a760-3167-40b4-a100-cd1590f76f66",
    email: "jay@leno.com",
    firstName: "Jay",
    middleName: "",
    lastName: "Leno",
    line1: "1149 Tower Road",
    line2: "",
    city: "Beverly Hills",
    state: "CA",
    postalCode: "90210",
    country: "USA",
    phone: "3025551212",
  },
  billing: {
    coreBills: [
      hdcPastDueBill,
      autopayCoreBill,
      autopayFailedCoreBill,
      pastDueCoreRevalBill,
      paidCoreBill,
    ],
    apexBills: [apexPastDueBill, apexAutopayFailedBill],
    alerts: {
      hdcAutopayFailed: true,
      hdcBillPastDue: true,
      insuranceAutopayFailed: [
        {
          source: "core",
          policyNumber: "abc123",
          state: "MD",
          country: "USA",
        },
        {
          source: "apex",
          policyNumber: "en02",
          state: "IL",
          country: "USA",
        },
      ],
      insuranceBillPastDue: [
        {
          source: "core",
          policyNumber: "def456",
          state: "MD",
          country: "USA",
        },
      ],
      eSignDocuments: [
        {
          id: 1,
          source: "usa",
          state: "MD",
        },
      ],
      paymentMethodExpired: [
        {
          source: "core",
          policyNumber: "abc123",
          last4: "1234",
          cardType: "Visa",
        },
      ],
      paymentMethodExpiring: [
        {
          source: "apex",
          policyNumber: "en02",
          last4: "5678",
          cardType: "Visa",
        },
      ],
    },
  },
  contactId: "JAY123",
  policies: [
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "abc123",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 5000,
      stateRegion: "MD",
      country: "USA",
      broker: {
        name: "Mr Broker Man",
        phone: "0001112233",
        phoneExtension: "123",
        line1: "1000 Broker Ln",
        line2: "",
        city: "Baltimore",
        state: "MD",
        postalCode: "03456",
        email: "mr.broker@example.com",
      },
      vehicles: [
        {
          id: 1,
          year: "1965",
          make: "Shelby",
          model: "Cobra 427",
          value: 1000000,
          vin: "1234567891",
          hasIdCard: true,
        },
        {
          id: 2,
          year: "1968",
          make: "Dodge",
          model: "Super Bee",
          value: 65000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyAddress,
      policyCoverages,
      additionalCoverages: [],
      paperlessDetails: {
        email: "jay@paperless.com",
        status: "ACTIVE",
      },
    },
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: true,
      policyStatus: "ISSUED",
      policyNumber: "def456",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 15000,
      stateRegion: "KS",
      country: "USA",
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 48400000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyAddress,
      policyCoverages,
      additionalCoverages: [],
      paperlessDetails: {
        email: "jay@paperless.com",
        status: "ACTIVE",
      },
    },
    {
      source: "APEX",
      hasIdCard: true,
      isAutoPay: true,
      stateRegion: "MI",
      policyStatus: "ISSUED",
      policyNumber: "en01",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 25000,
      country: "USA",
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 48400000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      paperlessDetails: {
        email: "jay@paperless.com",
        status: "ACTIVE",
      },
    },
    {
      source: "APEX",
      hasIdCard: true,
      isAutoPay: false,
      stateRegion: "IL",
      policyStatus: "ISSUED",
      policyNumber: "en02",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 15000,
      country: "USA",
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      vehicles: [
        {
          id: 1,
          year: "1965",
          make: "Ferrari",
          model: "350 GTO",
          value: 59500000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      paperlessDetails: {
        email: "jay@paperless.com",
        status: "ACTIVE",
      },
    },
  ],
  hdc: {
    hdcVersion: "1.5",
    membershipId: "HDC01981376",
    effectiveDate: "2020-01-20",
    expirationDate: "2021-01-20",
    benefits: { distance: 150 },
    membershipLevel: "2",
  },
  featureFlags: {},
};

const barackTrudeau: Persona = {
  key: "barackTrudeau",
  description:
    "🇺🇸/🇨🇦 | Multiple policies, one with broker | HDC 1.5 VIP | Insurance Autopay failed and HDC Autopay failed",
  customer: {
    id: "ef692b49-b390-4085-84ff-95f1f4e3a2fa",
    email: "barack@trudeau.com",
    firstName: "Barack",
    middleName: "",
    lastName: "Trudeau",
    line1: "1600 Pennsylvania Ave",
    line2: "",
    city: "Washington",
    state: "DC",
    postalCode: "01000",
    country: "USA",
    phone: "2023591000",
  },
  billing: {
    apexBills: [],
    coreBills: [
      hdcAutopayFailedBill,
      autopayCoreBill,
      autopayFailedCoreBill,
      pastDueCoreRevalBill,
      paidCoreBill,
    ],
    alerts: {
      hdcAutopayFailed: true,
      insuranceAutopayFailed: [
        {
          source: "core",
          policyNumber: "def456",
          state: "ON",
          country: "CANADA",
        },
      ],
    },
  },
  contactId: "BARACK1",
  policies: [
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "abc123",
      effectiveDate: "2025-12-25",
      expirationDate: "2026-11-30",
      premium: 5000,
      stateRegion: "DC",
      country: "USA",
      partner: "state-farm",
      broker: {
        name: "Mr Broker Man",
        phone: "0001112233",
        line1: "1000 Broker Ln",
        line2: "",
        city: "Baltimore",
        state: "MD",
        postalCode: "03456",
        email: "mr.broker@example.com",
      },
      vehicles: [
        {
          id: 1,
          year: "1965",
          make: "Shelby",
          model: "Cobra 427",
          value: 1000000,
          vin: "1234567890",
          hasIdCard: true,
        },
        {
          id: 2,
          year: "1968",
          make: "Dodge",
          model: "Super Bee",
          value: 65000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyAddress,
      policyCoverages,
      additionalCoverages: [],
      paperlessDetails: {},
    },
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "def456",
      effectiveDate: "2025-03-01",
      expirationDate: "2026-04-19",
      premium: 15000,
      stateRegion: "ON",
      country: "CANADA",
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 48400000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyAddress,
      policyCoverages,
      additionalCoverages: [],
      paperlessDetails: {},
    },
  ],
  hdc: {
    hdcVersion: "1.5",
    membershipId: "HDC01981371",
    effectiveDate: "2020-01-20",
    expirationDate: undefined,
    benefits: { distance: 150 },
    membershipLevel: "VIP",
  },
  featureFlags: {},
};

const janeDoe: Persona = {
  key: "janeDoe",
  description: "🇺🇸 | No policies | HDC 1.0 1st Gear",
  customer: {
    id: "270bc0f7-cc2f-49ac-8319-6a097568e13b",
    email: "jane@doe.com",
    firstName: "Jane",
    middleName: "Marie",
    lastName: "Doe",
    line1: "100 S. Main St",
    line2: "Apt 2B",
    city: "Motor City",
    state: "MI",
    postalCode: "40001-1234",
    country: "USA",
    phone: "2025551212",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {},
  },
  contactId: "JANE123",
  policies: [],
  hdc: {
    hdcVersion: "1.0",
    membershipId: "HDC04534125",
    expirationDate: "2022-04-27",
    effectiveDate: "2021-01-20",
    benefits: { distance: 100 },
    membershipLevel: "2",
  },
  featureFlags: {},
};

const dukeCaboom: Persona = {
  key: "dukeCaboom",
  description:
    "🇨🇦 | Single policy with no broker | HDC 1.0 VIP | Bill past due",
  customer: {
    id: "f50cff93-7316-4193-a4de-9f27e1b647c1",
    email: "duke@caboom.com",
    firstName: "Duke",
    middleName: "",
    lastName: "Caboom",
    line1: "54 Erie Dr",
    line2: "",
    city: "Montreal",
    state: "QC",
    postalCode: "G1A 0A6",
    country: "CANADA",
    phone: "5145551212",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {
      insuranceBillPastDue: [
        {
          source: "core",
          policyNumber: "dc101",
          state: "QC",
          country: "CANADA",
        },
      ],
    },
  },
  contactId: "DUKE123",
  policies: [
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "dc101",
      effectiveDate: "2021-12-25",
      expirationDate: "2022-11-30",
      premium: 51,
      stateRegion: "QC",
      country: "CANADA",
      vehicles: [
        {
          id: 1,
          year: "1936",
          make: "Indian",
          model: "Sport Scout Racer",
          value: 31250,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      broker: {
        name: "Olde Insurance",
        phone: "5145551313",
        line1: "123 Main St",
        line2: "Suite 201",
        city: "Montreal",
        state: "QC",
        postalCode: "G1A 0A6",
      },
      drivers,
      policyAddress,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {},
    },
  ],
  hdc: {
    hdcVersion: "1.0",
    membershipId: "HDC08923787",
    expirationDate: undefined,
    effectiveDate: "2022-06-20",
    benefits: { distance: 200 },
    membershipLevel: "VIP",
  },
  featureFlags: {},
};

const jonHomeless: Persona = {
  key: "jonHomeless",
  description:
    "🇨🇦 | No address or phone | Multiple policies with a broker/billing error | No HDC | 2 E-sign Alerts",
  customer: {
    id: "b0dfb2d0-93f9-4dd6-8899-1a771127f98d",
    email: "jon@homeless.com",
    firstName: "Jon",
    middleName: "",
    lastName: "Homeless",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "CANADA",
    phone: "",
  },
  billing: {
    coreBills: [],
    apexBills: [apexUpcomingBill],
    alerts: {
      eSignDocuments: [
        { id: 1, source: "usa", state: "MD" },
        { id: 2, source: "canada", state: "QC" },
      ],
    },
  },
  contactId: "JON123",
  policies: [
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "abc123",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 5000,
      stateRegion: "MD",
      country: "USA",
      broker: {
        name: "Mr Broker Man",
        phone: "0001112233",
        line1: "1000 Broker Ln",
        line2: "",
        city: "Baltimore",
        state: "MD",
        postalCode: "03456",
        email: "mr.broker@example.com",
      },
      vehicles: [
        {
          id: 1,
          year: "1965",
          make: "Shelby",
          model: "Cobra 427",
          value: 1000000,
          vin: "1234567890",
          hasIdCard: true,
        },
        {
          id: 2,
          year: "1968",
          make: "Dodge",
          model: "Super Bee",
          value: 65000,
          vin: "1234567891",
          hasIdCard: true,
        },
      ],
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {
        status: "INACTIVE",
      },
    },
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "dc101",
      effectiveDate: "2021-12-25",
      expirationDate: "2022-11-30",
      premium: 51,
      stateRegion: "QC",
      country: "CANADA",
      vehicles: [
        {
          id: 1,
          year: "1936",
          make: "Indian",
          model: "Sport Scout Racer",
          value: 31250,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      broker: {
        name: "Olde Insurance",
        phone: "5145551313",
        line1: "123 Main St",
        line2: "Suite 201",
        city: "Montreal",
        state: "QC",
        postalCode: "G1A 0A6",
      },
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {
        status: "INACTIVE",
      },
    },
    {
      source: "APEX",
      hasIdCard: true,
      isAutoPay: true,
      policyStatus: "ISSUED",
      stateRegion: "MI",
      policyNumber: "en01",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 15000,
      country: "USA",
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {
        status: "INACTIVE",
      },
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 484,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
    },
  ],
  hdc: null,
  featureFlags: {},
};

const markNobroker: Persona = {
  key: "markNobroker",
  description:
    "🇨🇦 | Multiple policies with no broker | HDC 1.0 AAA | ESign alert",
  customer: {
    id: "c57912ad-0bc7-49ac-a68e-2b8e5b3e3928",
    email: "mark@nobroker.com",
    firstName: "Mark",
    middleName: "",
    lastName: "Nobroker",
    line1: "54 Erie Dr",
    line2: "",
    city: "Montreal",
    state: "QC",
    postalCode: "G1A 0A6",
    country: "CANADA",
    phone: "5145551212",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {
      eSignDocuments: [{ id: 1, source: "canada", state: "QC" }],
    },
  },
  contactId: "MARK123",
  policies: [
    {
      source: "CORE",
      hasIdCard: false,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "dc101",
      effectiveDate: "2021-12-25",
      expirationDate: "2022-11-30",
      premium: 51,
      stateRegion: "QC",
      country: "CANADA",
      vehicles: [
        {
          id: 1,
          year: "1936",
          make: "Indian",
          model: "Sport Scout Racer",
          value: 31250,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {},
    },
    {
      source: "CORE",
      hasIdCard: true,
      isAutoPay: false,
      policyStatus: "ISSUED",
      policyNumber: "def456",
      effectiveDate: "2025-03-01",
      expirationDate: "2026-04-19",
      premium: 15000,
      stateRegion: "QC",
      country: "CANADA",
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 48400000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      paperlessDetails: {},
    },
  ],
  hdc: {
    hdcVersion: "1.0",
    membershipId: "HDC08923789",
    expirationDate: "2022-10-31",
    effectiveDate: "2021-10-31",
    benefits: undefined,
    membershipLevel: "AAA",
  },
  featureFlags: {},
};

const noFace: Persona = {
  key: "noFace",
  description:
    "🇺🇸 | No name, address, or phone number | No policies | No HDC | No alerts",
  customer: {
    id: "c01efaea-48ab-4d4b-b33f-e352118c0bag",
    email: "no@face.com",
    firstName: "",
    middleName: "",
    lastName: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "USA",
    phone: "",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {},
  },
  contactId: "",
  policies: [],
  hdc: null,
  featureFlags: {},
};

const tomNoPolicies: Persona = {
  key: "tomNoPolicies",
  description: "🇨🇦 | No policies | No HDC | No alerts",
  customer: {
    id: "c01efaea-48ab-4d4b-b33f-e352118c0baf",
    email: "tom@nopolicies.com",
    firstName: "Tom",
    middleName: "",
    lastName: "NoPolicies",
    line1: "54 Erie Dr",
    line2: "",
    city: "Montreal",
    state: "QC",
    postalCode: "G1A 0A6",
    country: "CANADA",
    phone: "5145551212",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {},
  },
  contactId: "TOM123",
  policies: [],
  hdc: null,
  featureFlags: {},
};

const lebron: Persona = {
  key: "lebron",
  description: "🇺🇸 | One enthusiast+ policy | HDC 1.5 3rd Gear",
  customer: {
    id: "96819923-1149-4dc6-9239-a93ef02ddadc",
    email: "lebron@nba.com",
    firstName: "LeBron",
    middleName: "",
    lastName: "James",
    line1: "123 king ln.",
    line2: "",
    city: "Cleveland",
    state: "OH",
    postalCode: "67349",
    country: "USA",
    phone: "3095554444",
  },
  billing: {
    apexBills: [apexAutopayBill],
    coreBills: [],
    alerts: {},
  },
  contactId: "BRON",
  policies: [
    {
      source: "APEX",
      hasIdCard: true,
      isAutoPay: true,
      policyStatus: "ISSUED",
      policyNumber: "en01",
      stateRegion: "OH",
      effectiveDate: "1999-01-01",
      expirationDate: "2999-12-31",
      premium: 15000,
      country: "USA",
      drivers,
      policyCoverages: [],
      additionalCoverages: [],
      vehicles: [
        {
          id: 1,
          year: "1962",
          make: "Ferrari",
          model: "250 GTO",
          value: 48400000,
          vin: "1234567890",
          hasIdCard: true,
        },
      ],
      paperlessDetails: {},
    },
  ],
  hdc: {
    hdcVersion: "1.5",
    membershipId: "HDC01981376",
    effectiveDate: "2020-01-20",
    expirationDate: "2021-01-20",
    benefits: { distance: 150 },
    membershipLevel: "2",
  },
  featureFlags: {},
};

const vivianWard: Persona = {
  key: "vivianWard",
  description: "🇺🇸 | No policies | Expired Card Alerts",
  customer: {
    id: "98cf8d0f-fab9-41e7-83a5-582890fabdb2",
    email: "vivian@ward.com",
    firstName: "Vivian",
    middleName: "Marie",
    lastName: "Ward",
    line1: "100 S. Main St",
    line2: "Apt 2B",
    city: "Motor City",
    state: "MI",
    postalCode: "40001-1234",
    country: "USA",
    phone: "2025551212",
  },
  billing: {
    apexBills: [],
    coreBills: [],
    alerts: {
      paymentMethodExpiring: [
        {
          source: "core",
          policyNumber: "def456",
          last4: "1234",
          cardType: "Visa",
        },
      ],
    },
  },
  contactId: "VIVIAN123",
  policies: [],
  hdc: null,
  featureFlags: {},
};

export const personas = {
  jayLeno,
  janeDoe,
  dukeCaboom,
  jonHomeless,
  markNobroker,
  noFace,
  tomNoPolicies,
  barackTrudeau,
  lebron,
  vivianWard,
};

export type PersonaKey = keyof typeof personas;
export type Personas = { [key in PersonaKey]: Persona };
